import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { useSearchQueryProject } from "../../hooks/useSearchQueryProject";
import { SearchableEntity } from "../../services/searchServiceProject";
import { commonTextFieldProps } from "../../../../utils/styles";

interface ProjectSearchableAutocompleteProps<T> {
  entityType: SearchableEntity;
  value: string;
  onSelect: (option: T | null) => void;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  getOptionLabel: (option: T | string) => string;
  renderOptionContent?: (option: T) => React.ReactNode;
  noOptionsText?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  projectId: string;
  allocationId?: string;
  error?: boolean;
  helperText?: string;
}

const ProjectSearchableAutocomplete = <T extends Record<string, any>>({
  entityType,
  value,
  onSelect,
  onChange,
  label,
  placeholder,
  getOptionLabel,
  renderOptionContent,
  noOptionsText = "No options found",
  disabled = false,
  size = "medium",
  projectId,
  allocationId,
  error,
  helperText,
}: ProjectSearchableAutocompleteProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: options = [], isLoading: loading } = useSearchQueryProject(
    entityType as SearchableEntity,
    searchTerm,
    projectId,
    allocationId
  );

  const debouncedSearch = useCallback(
    debounce((term: string) => setSearchTerm(term), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const defaultRenderOptionContent = (option: T) => (
    <>
      <Typography variant="body1">
        {option.name || option.petrolPumpName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {option.location && `${option.location} -${" "}`}
        {option.contactNumber ||
          option.phoneNumber ||
          option.primaryContactNumber}
      </Typography>
    </>
  );

  return (
    <Autocomplete<T | string>
      //   freeSolo
      options={options}
      getOptionLabel={getOptionLabel}
      loading={loading}
      value={value}
      onChange={(_, newValue) => {
        if (typeof newValue === "string") {
          onChange(newValue);
          debouncedSearch(newValue);
        } else if (newValue) {
          onSelect(newValue as T);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          onChange(newInputValue);
          debouncedSearch(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          placeholder={placeholder}
          sx={{ ...commonTextFieldProps.sx }}
          size={size}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Box fontSize={12}>
              {renderOptionContent
                ? renderOptionContent(option as T)
                : defaultRenderOptionContent(option as T)}
            </Box>
          </li>
        );
      }}
      noOptionsText={noOptionsText}
      disabled={disabled}
    />
  );
};

export default ProjectSearchableAutocomplete;
